import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { durationApi } from 'api/durationApi';

const DurationContext = createContext();

export const useDuration = () => {
  const context = useContext(DurationContext);
  if (!context) {
    throw new Error('useDuration must be used within DurationProvider');
  }
  return context;
};

export const DurationProvider = ({ children }) => {
  const [durations, setDurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchDurations = useCallback(async (forceRefresh = false) => {
    // Check cache if not forcing refresh
    if (!forceRefresh && durations.length > 0 && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return durations;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const options = {
        isActive: true,
        sortBy: 'displayOrder',
        sortOrder: 'ASC'
      };

      const response = await durationApi.getActiveDurations(options);
      
      if (response.durations) {
        // Sort: default first, then by display order
        const sortedDurations = [...response.durations].sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return a.displayOrder - b.displayOrder;
        });
        
        setDurations(sortedDurations);
        setLastFetchTime(Date.now());
        return sortedDurations;
      }
      
      return [];
    } catch (err) {
      console.error('Error fetching durations:', err);
      setError(err.message || 'Failed to load durations');
      return [];
    } finally {
      setLoading(false);
    }
  }, [durations, lastFetchTime]);

  const getDurationById = useCallback((id) => {
    return durations.find(d => d.id === id) || null;
  }, [durations]);

  const getDefaultDuration = useCallback(() => {
    return durations.find(d => d.isDefault) || (durations.length > 0 ? durations[0] : null);
  }, [durations]);

  const refreshDurations = useCallback(() => {
    return fetchDurations(true);
  }, [fetchDurations]);

  // Initial fetch on mount
  useEffect(() => {
    if (durations.length === 0) {
      fetchDurations();
    }
  }, []);

  const value = {
    durations,
    loading,
    error,
    fetchDurations,
    refreshDurations,
    getDurationById,
    getDefaultDuration
  };

  return (
    <DurationContext.Provider value={value}>
      {children}
    </DurationContext.Provider>
  );
};
